import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { Clock, LogIn, LogOut, Users, Check, CalendarOff, Plane } from 'lucide-react';
import { clsx } from 'clsx';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getActiveRestaurantId } from '../utils/roleAccess';
import { SkeletonTable, EmptyState } from '../components/LoadingStates';
import { attendanceService, Attendance, AttendanceStatus } from '../services/attendance';
import { organizationService } from '../services/organization';
import { employeeService } from '../services/employee';
import { Employee } from '../types';
import { Th } from './inventory/StockItemsTab';

const todayStr = () => new Date().toISOString().slice(0, 10);

const STATUS_BADGE: Record<AttendanceStatus, string> = {
  PRESENT: 'bg-green-100 text-green-800',
  ABSENT: 'bg-red-100 text-red-700',
  LEAVE: 'bg-amber-100 text-amber-800',
  HALF_DAY: 'bg-indigo-100 text-indigo-800',
};

const empName = (e: Employee) => e.name || `${e.firstName ?? ''} ${e.lastName ?? ''}`.trim() || e.email;

function fmtClock(iso?: string) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}
function fmtHours(mins?: number) {
  if (!mins || mins <= 0) return '—';
  const h = Math.floor(mins / 60), m = mins % 60;
  return `${h}h ${m}m`;
}

export const AttendancePage: React.FC = () => {
  const { user } = useAuth();
  const { currentTheme } = useTheme();
  const queryClient = useQueryClient();
  const org = user?.organizationCode;
  const restaurantId = getActiveRestaurantId() || undefined;
  const [date, setDate] = useState(todayStr());

  // Resolve org id, then employees assigned to the active restaurant
  const { data: orgDoc } = useQuery(['organization', org], () => organizationService.getOrganizationByCode(org!), { enabled: !!org });
  const { data: employees, isLoading: empLoading } = useQuery(
    ['employees', orgDoc?.id],
    () => employeeService.getEmployeesByOrganization(orgDoc!.id),
    { enabled: !!orgDoc?.id },
  );
  const { data: records, isLoading: recLoading } = useQuery(
    ['attendance', org, restaurantId, date],
    () => attendanceService.list(org!, restaurantId, date),
    { enabled: !!org },
  );

  const staff = useMemo(() => {
    const list = employees ?? [];
    if (!restaurantId) return list.filter(e => e.active !== false);
    return list.filter(e => e.active !== false && (e.restaurantRoles || []).some(rr => rr.restaurantId === restaurantId));
  }, [employees, restaurantId]);

  const recordByEmp = useMemo(() => {
    const map = new Map<string, Attendance>();
    (records ?? []).forEach(r => map.set(r.employeeId, r));
    return map;
  }, [records]);

  const invalidate = () => { queryClient.invalidateQueries(['attendance']); };

  const clockInMutation = useMutation(
    (e: Employee) => attendanceService.clockIn(org!, restaurantId, e.id, empName(e)),
    { onSuccess: () => { invalidate(); toast.success('Clocked in'); }, onError: (er: any) => { toast.error(er.response?.data?.message || 'Failed'); } },
  );
  const clockOutMutation = useMutation((id: string) => attendanceService.clockOut(id),
    { onSuccess: () => { invalidate(); toast.success('Clocked out'); }, onError: (er: any) => { toast.error(er.response?.data?.message || 'Failed'); } });
  const markMutation = useMutation(
    ({ e, status }: { e: Employee; status: AttendanceStatus }) =>
      attendanceService.mark({ organizationCode: org!, restaurantId, employeeId: e.id, employeeName: empName(e), date, status }),
    { onSuccess: () => { invalidate(); toast.success('Marked'); }, onError: (er: any) => { toast.error(er.response?.data?.message || 'Failed'); } },
  );

  const summary = useMemo(() => {
    const recs = records ?? [];
    const present = recs.filter(r => r.status === 'PRESENT').length;
    const onLeave = recs.filter(r => r.status === 'LEAVE').length;
    const totalMins = recs.reduce((s, r) => s + (r.workedMinutes || 0), 0);
    return { present, onLeave, totalMins, marked: recs.length };
  }, [records]);

  const isToday = date === todayStr();
  const isLoading = empLoading || recLoading;

  if (!org) {
    return <div className="p-6"><EmptyState icon={<Clock className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />} title="No organization selected" description="Your account is not linked to an organization." /></div>;
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex items-center gap-3.5">
          <div className="grid place-items-center h-12 w-12 rounded-2xl text-white shrink-0" style={{ background: `linear-gradient(135deg, ${currentTheme.colors.primary[500]}, ${currentTheme.colors.primary[700]})`, boxShadow: `0 14px 32px -12px ${currentTheme.colors.primary[500]}` }}>
            <Clock className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight" style={{ color: currentTheme.colors.text.primary }}>Staff Attendance</h1>
            <p style={{ color: currentTheme.colors.text.secondary }}>
              {summary.present} present · {summary.onLeave} on leave · {fmtHours(summary.totalMins)} total
            </p>
          </div>
        </div>
        <div className="mt-3 sm:mt-0">
          <label className="block text-xs font-medium mb-1" style={{ color: currentTheme.colors.text.secondary }}>Date</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg" />
        </div>
      </div>

      {isLoading ? (
        <SkeletonTable rows={6} columns={5} />
      ) : staff.length === 0 ? (
        <EmptyState icon={<Users className="h-16 w-16" style={{ color: currentTheme.colors.text.secondary }} />} title="No staff" description="No employees assigned to this restaurant." />
      ) : (
        <div className="overflow-x-auto rounded-xl border" style={{ backgroundColor: currentTheme.colors.background.card, borderColor: currentTheme.colors.border.primary }}>
          <table className="min-w-full divide-y" style={{ borderColor: currentTheme.colors.border.primary }}>
            <thead className="ui-thead">
              <tr><Th>Employee</Th><Th>Status</Th><Th>In</Th><Th>Out</Th><Th>Hours</Th><Th className="text-right">Actions</Th></tr>
            </thead>
            <tbody className="ui-tbody">
              {staff.map(e => {
                const rec = recordByEmp.get(e.id);
                const clockedIn = rec?.clockIn && !rec?.clockOut;
                return (
                  <tr key={e.id}>
                    <td className="px-4 py-3 text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>{empName(e)}</td>
                    <td className="px-4 py-3">
                      {rec ? <span className={clsx('inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium', STATUS_BADGE[rec.status])}>{rec.status.replace('_', ' ')}</span>
                        : <span className="text-xs" style={{ color: currentTheme.colors.text.secondary }}>Not marked</span>}
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}>{fmtClock(rec?.clockIn)}</td>
                    <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.secondary }}>{fmtClock(rec?.clockOut)}</td>
                    <td className="px-4 py-3 text-sm" style={{ color: currentTheme.colors.text.primary }}>{fmtHours(rec?.workedMinutes)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        {isToday && !rec && (
                          <button onClick={() => clockInMutation.mutate(e)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium" style={{ color: '#16a34a', backgroundColor: '#f0fdf4' }}>
                            <LogIn className="h-3.5 w-3.5" /> Clock In
                          </button>
                        )}
                        {isToday && clockedIn && (
                          <button onClick={() => clockOutMutation.mutate(rec!.id)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium" style={{ color: '#b45309', backgroundColor: '#fef3c7' }}>
                            <LogOut className="h-3.5 w-3.5" /> Clock Out
                          </button>
                        )}
                        {rec?.clockOut && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs" style={{ color: '#16a34a' }}><Check className="h-3.5 w-3.5" /> Done</span>
                        )}
                        {!rec && (
                          <>
                            <button onClick={() => markMutation.mutate({ e, status: 'LEAVE' })} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium" style={{ color: '#b45309', backgroundColor: '#fffbeb' }}>
                              <Plane className="h-3.5 w-3.5" /> Leave
                            </button>
                            <button onClick={() => markMutation.mutate({ e, status: 'ABSENT' })} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium" style={{ color: '#dc2626', backgroundColor: '#fef2f2' }}>
                              <CalendarOff className="h-3.5 w-3.5" /> Absent
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
